import { useQuery } from '@tanstack/react-query';
import { keepPreviousData } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExportButton } from '@/components/common/ExportButton';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { cadePositioningParams, fetchCadePositioning } from '@/services/cade';
import type { CadePlacement, CadePositionList } from '@/services/types';

const CONDUCTS = ['cartel', 'unilateral', 'uniforme'] as const;

/**
 * Where a matter falls against the register's similar protocols.
 *
 * The LIST of matching protocols is the answer and is always shown; the placement above it
 * (quantiles, and where a typed amount would fall) appears only when enough protocols carry a
 * fine. The register codes the kind of conduct and the procedure and nothing of revenue, so
 * revenue is not asked and the page says why; the number of parties is the register's party rows
 * per protocol. A second ladder places a TCC contribution among every TCC in the register, which
 * the conduct filters cannot narrow. A placement near the middle says nothing about whether a
 * fine was proportionate.
 */
interface Filters {
  conducts: string[];
  procedure: string | null;
  amountParam: string;
  amount: number | null;
  tccParam: string;
  tccAmount: number | null;
  partiesMin: number | null;
  partiesMax: number | null;
}

type FilterChange = {
  conducts?: string[];
  procedure?: string | null;
  amount?: string;
  tcc?: string;
  partiesMin?: number | null;
  partiesMax?: number | null;
};

/** A non-negative number from the address, or null. */
function nonNegative(value: string | null): number | null {
  if (!value) return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** A typed amount in Brazilian or plain notation, as the address holds it. */
function plainAmount(typed: string): string {
  return typed.replace(/\./g, '').replace(',', '.');
}

/** The filters as the address holds them; an unknown conduct or a negative amount is dropped. */
function readFilters(params: URLSearchParams): Filters {
  const amountParam = params.get('amount') ?? '';
  const tccParam = params.get('tcc') ?? '';
  const partiesMin = nonNegative(params.get('parties_min'));
  const partiesMax = nonNegative(params.get('parties_max'));
  return {
    conducts: params.getAll('conduct').filter((c) => (CONDUCTS as readonly string[]).includes(c)),
    procedure: params.get('procedure'),
    amountParam,
    amount: nonNegative(amountParam),
    tccParam,
    tccAmount: nonNegative(tccParam),
    partiesMin: partiesMin === null ? null : Math.floor(partiesMin),
    partiesMax: partiesMax === null ? null : Math.floor(partiesMax),
  };
}

/** The address for the current filters with some of them replaced. */
function withFilters(current: Filters, next: FilterChange): URLSearchParams {
  const out = new URLSearchParams();
  for (const c of next.conducts ?? current.conducts) out.append('conduct', c);
  const procedure = next.procedure === undefined ? current.procedure : next.procedure;
  if (procedure) out.set('procedure', procedure);
  const amount = next.amount ?? current.amountParam;
  if (nonNegative(amount) !== null) out.set('amount', amount);
  const partiesMin = next.partiesMin === undefined ? current.partiesMin : next.partiesMin;
  if (partiesMin !== null) out.set('parties_min', String(partiesMin));
  const partiesMax = next.partiesMax === undefined ? current.partiesMax : next.partiesMax;
  if (partiesMax !== null) out.set('parties_max', String(partiesMax));
  const tcc = next.tcc ?? current.tccParam;
  if (nonNegative(tcc) !== null) out.set('tcc', tcc);
  return out;
}

export function CadePositioningPage() {
  const { t, i18n } = useTranslation(['cade']);
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);
  const { conducts, procedure, amount, tccAmount, partiesMin, partiesMax } = filters;
  const [typedAmount, setTypedAmount] = useState(filters.amountParam);
  const [typedTcc, setTypedTcc] = useState(filters.tccParam);
  const [typedMin, setTypedMin] = useState(partiesMin === null ? '' : String(partiesMin));
  const [typedMax, setTypedMax] = useState(partiesMax === null ? '' : String(partiesMax));

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'positioning', conducts, procedure, amount, tccAmount, partiesMin, partiesMax],
    queryFn: () =>
      fetchCadePositioning({ conducts, procedure, amount, tccAmount, partiesMin, partiesMax }),
    placeholderData: keepPreviousData,
  });

  const update = (next: FilterChange) => setParams(withFilters(filters, next));
  const toggle = (conduct: string) =>
    update({
      conducts: conducts.includes(conduct)
        ? conducts.filter((c) => c !== conduct)
        : [...conducts, conduct],
    });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    update({
      amount: plainAmount(typedAmount),
      tcc: plainAmount(typedTcc),
      partiesMin: nonNegative(typedMin) === null ? null : Math.floor(Number(typedMin)),
      partiesMax: nonNegative(typedMax) === null ? null : Math.floor(Number(typedMax)),
    });
  };
  const money = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

  return (
    <div className="space-y-4 p-6" data-positioning>
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:positioning.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:positioning.subtitle')}</p>
      </header>
      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      <form onSubmit={submit} className="space-y-3 rounded-md border p-3" role="search">
        <fieldset className="flex flex-wrap items-center gap-4">
          <legend className="mb-1 text-xs font-medium text-muted-foreground">
            {t('cade:positioning.conduct')}
          </legend>
          {CONDUCTS.map((conduct) => (
            <label key={conduct} className="flex items-center gap-1.5 text-sm text-foreground">
              <input
                type="checkbox"
                checked={conducts.includes(conduct)}
                onChange={() => toggle(conduct)}
              />
              {t(`cade:positioning.conducts.${conduct}`)}
            </label>
          ))}
        </fieldset>
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1 text-xs text-muted-foreground">
            {t('cade:positioning.procedure')}
            <select
              value={procedure ?? ''}
              onChange={(event) => update({ procedure: event.target.value || null })}
              className="block rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
            >
              <option value="">{t('cade:positioning.anyProcedure')}</option>
              {(data?.procedures ?? []).map((option) => (
                <option key={option.procedure} value={option.procedure}>
                  {option.procedure} ({option.protocols})
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-xs text-muted-foreground">
            {t('cade:positioning.amount')}
            <input
              inputMode="decimal"
              value={typedAmount}
              onChange={(event) => setTypedAmount(event.target.value)}
              placeholder="5000000"
              className="block w-44 rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
            />
          </label>
          <label className="space-y-1 text-xs text-muted-foreground">
            {t('cade:positioning.tccAmount')}
            <input
              inputMode="decimal"
              value={typedTcc}
              onChange={(event) => setTypedTcc(event.target.value)}
              placeholder="1000000"
              className="block w-44 rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
              data-tcc-input
            />
          </label>
          <fieldset className="space-y-1">
            <legend className="text-xs text-muted-foreground">
              {t('cade:positioning.parties')}
            </legend>
            <div className="flex items-center gap-1">
              <input
                inputMode="numeric"
                value={typedMin}
                onChange={(event) => setTypedMin(event.target.value)}
                placeholder={t('cade:positioning.partiesMin')}
                aria-label={t('cade:positioning.partiesMin')}
                className="block w-20 rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
                data-parties-min
              />
              <span className="text-xs text-muted-foreground">–</span>
              <input
                inputMode="numeric"
                value={typedMax}
                onChange={(event) => setTypedMax(event.target.value)}
                placeholder={t('cade:positioning.partiesMax')}
                aria-label={t('cade:positioning.partiesMax')}
                className="block w-20 rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
                data-parties-max
              />
            </div>
          </fieldset>
          <button
            type="submit"
            className="rounded-md bg-primary px-4 py-1.5 text-sm text-primary-foreground"
          >
            {t('cade:positioning.place')}
          </button>
        </div>
        <p className="text-xs text-muted-foreground" data-not-coded>
          {t('cade:positioning.notCoded')}
        </p>
      </form>

      {isPending ? <SkeletonLoader rows={6} /> : null}
      {error ? <ErrorMessage message={error.message} onRetry={() => void refetch()} /> : null}
      {data ? (
        <>
          <section className="space-y-2" data-placement>
            <h2 className="text-sm font-semibold text-foreground">
              {t('cade:positioning.placementTitle')}
            </h2>
            <Answer answer={data.placement} help={t('cade:positioning.placementHelp')}>
              {(placement: CadePlacement) => (
                <PlacementCard placement={placement} money={(n) => money.format(n)} />
              )}
            </Answer>
          </section>
          {data.tcc ? (
            <section className="space-y-2" data-tcc-placement>
              <h2 className="text-sm font-semibold text-foreground">
                {t('cade:positioning.tccTitle')}
              </h2>
              <Answer answer={data.tcc} help={t('cade:positioning.tccHelp')}>
                {(placement: CadePlacement) => (
                  <PlacementCard placement={placement} money={(n) => money.format(n)} kind="tcc" />
                )}
              </Answer>
            </section>
          ) : null}
          <section className="space-y-2" data-cases>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-foreground">
                {t('cade:positioning.casesTitle')}
              </h2>
              <ExportButton
                path={`/api/v1/cade/export/positioning?${cadePositioningParams({ conducts, procedure, partiesMin, partiesMax }).toString()}`}
              />
            </div>
            <Answer answer={data.cases} help={t('cade:positioning.casesHelp')}>
              {(list: CadePositionList) => <CaseTable list={list} money={(n) => money.format(n)} />}
            </Answer>
          </section>
        </>
      ) : null}
    </div>
  );
}

export function PlacementCard({
  placement,
  money,
  kind = 'fine',
}: {
  placement: CadePlacement;
  money: (n: number) => string;
  /** A fine ladder or a TCC-contribution ladder: the words differ, never the arithmetic. */
  kind?: 'fine' | 'tcc';
}) {
  const prefix = kind === 'tcc' ? 'cade:positioning.tcc' : 'cade:positioning';
  const { t } = useTranslation(['cade']);
  const rungs: [string, number][] = [
    ['p25', placement.p25],
    ['median', placement.median],
    ['p75', placement.p75],
    ['p90', placement.p90],
    ['maximum', placement.maximum],
  ];
  return (
    <div className="space-y-2 rounded-md border p-3">
      <p className="text-xs text-muted-foreground">
        {t(`${prefix}.ladderOf`, { count: placement.n })}
      </p>
      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {rungs.map(([key, value]) => (
          <div key={key}>
            <dt className="text-xs text-muted-foreground">{t(`cade:positioning.rung.${key}`)}</dt>
            <dd className="tabular-nums text-sm text-foreground">{money(value)}</dd>
          </div>
        ))}
      </dl>
      {placement.amount !== null && placement.below !== null ? (
        <p className="text-sm text-foreground" data-amount-placement>
          {t(`${prefix}.amountPlacement`, {
            amount: money(placement.amount),
            below: placement.below,
            count: placement.n,
          })}
        </p>
      ) : null}
    </div>
  );
}

function CaseTable({ list, money }: { list: CadePositionList; money: (n: number) => string }) {
  const { t } = useTranslation(['cade']);
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {t('cade:positioning.reach', {
          total: list.total,
          fined: list.with_fine,
          decided: list.decided,
          listed: list.rows.length,
        })}
      </p>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-foreground">
            <tr>
              {['number', 'procedure', 'conduct', 'sg', 'tribunal', 'year', 'fine'].map((col) => (
                <th key={col} className="px-3 py-2">
                  {t(`cade:positioning.columns.${col}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.rows.map((row) => (
              <tr key={row.case_number} className="border-t align-top" data-position-row>
                <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                  {row.case_key ? (
                    <Link
                      to={`/cade/c/${row.case_key}`}
                      className="text-primary underline-offset-4 hover:underline"
                    >
                      {row.case_number}
                    </Link>
                  ) : (
                    <span className="text-foreground">{row.case_number}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{row.procedure ?? '—'}</td>
                <td className="px-3 py-2 text-xs text-foreground">
                  {row.conducts.map((c) => t(`cade:positioning.conducts.${c}`)).join(', ')}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{row.sg_outcome ?? '—'}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {row.tribunal_outcome ?? '—'}
                </td>
                <td className="px-3 py-2 tabular-nums text-xs text-muted-foreground">
                  {row.decision_year ?? '—'}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-foreground">
                  {row.fine_brl !== null ? money(row.fine_brl) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
